/**
 * Menu Items Page Component
 * Main page for managing menu items, categories, and addons
 */

import {
  cleanSearchString,
  prepareMenuItemsCSVData,
} from '@/renderer/lib/utils';
import { fetchAllMenuItemsForExport } from '@/renderer/services/menuServicePaginated';
import { apiError } from '@/renderer/util/notify';
import {
  Input,
  OutletIndicator,
  ReadOnlyAccessNotice,
  TabButton,
} from '@components/common';
import { useAddons, useCategories, useMenuCounts } from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import { selectCanManageMenu } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronDown,
  Download,
  Filter,
  Grid3X3,
  List,
  Package,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Tag,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { useOnboardingDriverTour } from '../../../hooks/useOnboardingDriverTour';
import {
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../../services/onboardingTourService';
import { Addons } from './Addons';
import { AIMenuImport } from './AIMenuImport';
import { Categories } from './Categories';
import { MenuItems, MenuItemsRef } from './MenuItems';
import { StatsCard } from './StatsCard';

type ViewMode = 'grid' | 'list';
type ActiveTab = 'items' | 'categories' | 'addons' | 'ai-import';

export const MenuItemsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const canManageMenu = useAppSelector(selectCanManageMenu);

  const { data: allCategories = [], error: categoriesError } =
    useCategories(outletId);
  const activeCategories = allCategories.filter((c) => c.status === 'active');
  const { data: menuCounts, error: countsError } = useMenuCounts(outletId);
  const { error: addonsError } = useAddons(outletId);

  const error =
    categoriesError?.message ??
    countsError?.message ??
    addonsError?.message ??
    null;

  const stats = {
    totalItems: menuCounts?.totalItems ?? 0,
    totalCategories: menuCounts?.totalCategories ?? 0,
    totalAddons: menuCounts?.totalAddons ?? 0,
    outOfStockItems: menuCounts?.outOfStockItems ?? 0,
  };

  const [activeTab, setActiveTab] = useState<ActiveTab>('items');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<
    string | null
  >(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [page, setPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState<string>('');
  const categorySearchQuery = useMemo(
    () => cleanSearchString(categorySearch),
    [categorySearch],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategoryFilter]);

  const menuItemsRef = useRef<MenuItemsRef>(null);

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.menuPage),
        popover: {
          title: 'Build your menu here',
          description:
            'This is where you manage categories, menu items, addons, and AI-assisted imports for the selected outlet.',
          side: 'bottom' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.menuCategoriesTab,
        ),
        popover: {
          title: 'Organize items with categories',
          description:
            'Use categories to group menu items in a clean way before the sales team starts taking orders.',
          side: 'bottom' as const,
        },
      },
      {
        element: onboardingTourSelector(
          ONBOARDING_TOUR_TARGETS.menuAddItemButton,
        ),
        popover: {
          title: 'Add a menu item',
          description:
            'Create the items you want to sell here, attach them to departments, and make sure pricing is ready for the sales screen.',
          side: 'left' as const,
        },
      },
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.menuItemsGrid),
        popover: {
          title: 'Review the live item list',
          description:
            'Your created menu items appear here and become selectable later on the Sales page.',
          side: 'top' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({ stepId: 'menu', steps: onboardingTourSteps });

  const handleAddItemClick = () => {
    if (!outletId || !canManageMenu) return;
    if (activeTab === 'items') {
      menuItemsRef.current?.openCreateModal();
    }
  };

  const handleImportWithAI = () => {
    if (!canManageMenu) return;
    setActiveTab('ai-import');
  };

  const handleAIImportComplete = () => {
    setActiveTab('items');
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleReload = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.menu.all });
  };

  const handleExportCSV = async () => {
    if (!outletId) return;
    setIsExporting(true);
    try {
      const result = await fetchAllMenuItemsForExport(outletId);
      if (!result.success || !result.data) {
        apiError('Failed to fetch menu items for export.');
        return;
      }

      const items = result.data;
      if (items.length === 0) {
        apiError('No menu items to export.');
        return;
      }

      const headers = [
        'Name',
        'Category',
        'Department',
        'Price',
        'Status',
        'Taxable',
        'SKU',
      ];
      const csvRows = [headers.join(',')];
      const formattedRows = prepareMenuItemsCSVData(items);

      const csvString = [csvRows, ...formattedRows].join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `menu_items_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'An error occurred during export.',
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="space-y-6"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.menuPage}
    >
      <OutletIndicator description="Viewing menu data across all outlets. Select a specific outlet before creating a category, item, or addon." />

      {!canManageMenu && (
        <ReadOnlyAccessNotice message="You can view menu data here, but only authorized roles can create, update, or delete menu records." />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {activeTab === 'items'
              ? 'Menu Items'
              : activeTab === 'categories'
                ? 'Categories'
                : activeTab === 'addons'
                  ? 'Addons Library'
                  : 'AI Menu Import'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {activeTab === 'items'
              ? 'Manage your menu items'
              : activeTab === 'categories'
                ? 'Manage menu categories'
                : activeTab === 'addons'
                  ? 'Manage reusable addons'
                  : 'Import menu from documents'}
          </p>
        </div>

        {activeTab === 'items' && (
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search items..."
                className="pl-8 pr-7 py-1.5 h-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary w-52"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {canManageMenu ? (
              <>
                <button
                  type="button"
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm h-9 disabled:opacity-50"
                  title="Export to CSV"
                >
                  <Download size={16} />
                  <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleImportWithAI}
                  className="flex items-center gap-2 px-3 py-1.5 border border-pos-primary text-pos-primary rounded-lg hover:bg-pos-light/10 transition-colors text-sm h-9"
                >
                  <Sparkles size={16} />
                  <span>Import with AI</span>
                </button>
              </>
            ) : null}

            <button
              type="button"
              onClick={handleReload}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm h-9"
              title="Reload Data"
            >
              <RefreshCw size={16} />
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg hover:bg-gray-50 text-sm transition-colors ${
                  selectedCategoryFilter
                    ? 'border-pos-primary text-pos-primary bg-blue-50'
                    : 'border-gray-300 text-gray-700'
                }`}
              >
                <Filter size={16} />
                <span>
                  {selectedCategoryFilter
                    ? activeCategories.find(
                        (c) => c.id === selectedCategoryFilter,
                      )?.name || 'Filtered'
                    : 'Filter'}
                </span>
                <ChevronDown size={14} />
              </button>

              {showCategoryDropdown && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 max-h-[500px] overflow-y-auto">
                  <Input
                    value={categorySearch}
                    onChange={(val) => setCategorySearch(val)}
                    placeholder="Search"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategoryFilter(null);
                      setShowCategoryDropdown(false);
                    }}
                    className={`nav-link w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!selectedCategoryFilter ? 'text-pos-primary font-medium' : 'text-gray-700'}`}
                  >
                    All Items
                  </button>
                  {activeCategories
                    .filter((item) => {
                      const cleanTitle = cleanSearchString(item.name);

                      return cleanTitle.includes(categorySearchQuery);
                    })
                    .map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          setSelectedCategoryFilter(category.id);
                          setShowCategoryDropdown(false);
                        }}
                        className={`nav-link w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${selectedCategoryFilter === category.id ? 'text-pos-primary font-medium' : 'text-gray-700'}`}
                      >
                        {category.name}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden h-9">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'}`}
                title="Grid View"
              >
                <Grid3X3 size={16} />
              </button>
              <div className="w-px h-full bg-gray-300" />
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:bg-gray-50'}`}
                title="List View"
              >
                <List size={16} />
              </button>
            </div>

            {canManageMenu ? (
              <button
                type="button"
                onClick={handleAddItemClick}
                disabled={!outletId}
                data-onboarding-tour={ONBOARDING_TOUR_TARGETS.menuAddItemButton}
                className="flex items-center gap-2 px-3 py-1.5 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors text-sm h-9 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={16} />
                <span>Add Item</span>
              </button>
            ) : null}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatsCard
          value={stats.totalItems}
          label="Total Items"
          icon={<Package size={20} className="text-blue-600" />}
          iconBgColor="bg-blue-100"
        />
        <StatsCard
          value={stats.totalCategories}
          label="Categories"
          icon={<Tag size={20} className="text-green-600" />}
          iconBgColor="bg-green-100"
        />
        <StatsCard
          value={stats.totalAddons}
          label="Total Addons"
          icon={<Package size={20} className="text-purple-600" />}
          iconBgColor="bg-purple-100"
        />
        <StatsCard
          value={stats.outOfStockItems}
          label="Out of Stock"
          icon={<AlertCircle size={20} className="text-amber-600" />}
          iconBgColor="bg-amber-100"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <TabButton
            label="Menu Items"
            isActive={activeTab === 'items'}
            onClick={() => setActiveTab('items')}
          />
          <TabButton
            label="Categories"
            isActive={activeTab === 'categories'}
            onClick={() => setActiveTab('categories')}
            dataTourId={ONBOARDING_TOUR_TARGETS.menuCategoriesTab}
          />
          <TabButton
            label="Addons Library"
            isActive={activeTab === 'addons'}
            onClick={() => setActiveTab('addons')}
          />
          {activeTab === 'ai-import' && (
            <TabButton
              label="AI Import"
              isActive
              onClick={() => setActiveTab('ai-import')}
            />
          )}
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div
        className="mt-6 max-h-[calc(100vh-400px)] overflow-y-auto"
        data-onboarding-tour={ONBOARDING_TOUR_TARGETS.menuItemsGrid}
      >
        {activeTab === 'items' && (
          <MenuItems
            ref={menuItemsRef}
            canManageMenu={canManageMenu}
            viewMode={viewMode}
            categoryFilter={selectedCategoryFilter}
            searchQuery={debouncedSearch}
            page={page}
            onPageChange={setPage}
            onImportWithAI={handleImportWithAI}
          />
        )}
        {activeTab === 'categories' && <Categories />}
        {activeTab === 'addons' && <Addons />}
        {activeTab === 'ai-import' && (
          <AIMenuImport
            onComplete={handleAIImportComplete}
            onCancel={() => setActiveTab('items')}
          />
        )}
      </div>
    </div>
  );
};
